/**
 * Scoring Component Mappings
 *
 * This file contains the mappings between sport names and their scoring components.
 * Separated from scoringConfig.js to avoid circular dependencies.
 */

import PokerScoring from './poker/PokerScoring';
import PickleballScoring from './pickleball/PickleballScoring';
import CricketScoring from './cricket/CricketScoring';
import BasketballScoring from './basketball/BasketballScoring';
import BasketballGameView from './basketball/BasketballGameView';
import VolleyballScoring from './volleyball/VolleyballScoring';
import PingPongScoring from './pingpong/PingPongScoring';
import TennisScoring from './tennis/TennisScoring';
import BadmintonScoring from './badminton/BadmintonScoring';
import { sportTypeAPI } from '../services/api';

/**
 * Mapping of sport name (lowercase) to scoring components.
 * Keyed by sport name so it works regardless of which IDs a DB assigns.
 */
const SCORING_COMPONENT_BY_NAME = {
  'Poker':       PokerScoring,
  'Pickleball':  PickleballScoring,
  'Cricket':     CricketScoring,
  'Basketball':  BasketballScoring,
  'Volleyball':  VolleyballScoring,
  'Ping Pong':   PingPongScoring,
  'Tennis':      TennisScoring,
  'Badminton':   BadmintonScoring,
};

/**
 * Get the scoring component for a given sport type ID.
 * @param {number|string} sportTypeId
 * @param {string} [tabName] - Optional tab context (e.g. 'pastGames')
 * @returns {Promise<React.Component|null>}
 */
export const getScoringComponent = async (sportTypeId, tabName) => {
  const name = await getSportName(sportTypeId);
  if (!name) return null;
  if (name === 'Basketball' && tabName === 'pastGames') return BasketballGameView;
  return SCORING_COMPONENT_BY_NAME[name] || null;
};

/**
 * Check if a scoring card is available for a given sport type ID.
 * @param {number|string} sportTypeId
 * @returns {Promise<boolean>}
 */
export const hasScoringCard = async (sportTypeId) => {
  return (await getScoringComponent(sportTypeId)) !== null;
};

/**
 * Synchronous version of hasScoringCard — uses the local name map
 * so it can be called safely from render without returning a Promise.
 * @param {number|string} sportTypeId
 * @returns {boolean}
 */
export const hasScoringCardSync = (sportTypeId) => {
  const name = getSportNameSync(sportTypeId);
  if (!name) return false;
  return !!SCORING_COMPONENT_BY_NAME[name];
};

/**
 * Synchronous sport-name lookup by ID using a well-known map.
 * Falls back to null (never makes an API call), so it's safe to use in render.
 */
const SPORT_ID_TO_NAME = {
  1: 'Poker',
  2: 'Pickleball',
  3: 'Cricket',
  4: 'Basketball',
  5: 'Volleyball',
  6: 'Ping Pong',
  7: 'Tennis',
  8: 'Badminton',
};

export const getSportNameSync = (sportTypeId) => {
  if (!sportTypeId) return null;
  return SPORT_ID_TO_NAME[sportTypeId] || null;
};

export const getSportName = async (sportTypeId) => {
  // Try sync lookup first
  const syncName = getSportNameSync(sportTypeId);
  if (syncName) return syncName;

  if (!sportTypeId) return null;
  var name = sportTypeId;
  try {
    const result = await sportTypeAPI.getById(sportTypeId);
    name = result?.sportName || result?.data?.sportName;

    if (!name) return null;
    return name;
  } catch (err) {
    console.error('[getScoringComponent] Failed to fetch sport type:', sportTypeId, err);
    return null;
  }
}

/**
 * Generate a display name for a game based on sport name and today's date.
 * Format: "Poker-02/11/2026", "Cricket-02/11/2026", etc.
 * @param {string} sportName - e.g. 'poker', 'cricket', 'ping pong'
 * @returns {string}
 */
export const generateGameName = (sportName) => {
  const name = sportName
    ? sportName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : 'Game';
  const today = new Date();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const yyyy = today.getFullYear();
  return `${name}-${mm}/${dd}/${yyyy}`;
};
